import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Progress,
  Container,
  Heading,
  useColorModeValue,
  IconButton,
  useBreakpointValue,
  Image,
  SimpleGrid,
  Icon,
  Flex,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaUsers, FaVial, FaHospital, FaUserMd, FaMapMarkerAlt, FaClock, FaShieldAlt, FaDollarSign, FaChartLine, FaClipboardList, FaCheckCircle, FaUserTie } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';

const MotionBox = motion(Box);

const PitchDeck = ({ userEmail, trackPageView }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [liveStats, setLiveStats] = useState({
    bloodDraws: 0,
    phlebotomists: 0,
    doctors: 0,
    patients: 0,
    loading: true
  });
  const [animatedStats, setAnimatedStats] = useState({
    bloodDraws: 0,
    phlebotomists: 0,
    doctors: 0,
    patients: 0
  });

  const bgGradient = useColorModeValue(
    'linear(45deg, #0f0f23 0%, #1a1a3a 25%, #2d1b69 50%, #1a1a3a 75%, #0f0f23 100%)',
    'linear(45deg, #0f0f23 0%, #1a1a3a 25%, #2d1b69 50%, #1a1a3a 75%, #0f0f23 100%)'
  );
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.98)', 'rgba(255, 255, 255, 0.98)');
  const textColor = useColorModeValue('gray.800', 'gray.800');
  const accentColor = useColorModeValue('#0066cc', '#0066cc');

  // Dramatic number counting animation
  const animateNumber = useCallback((target, key, duration = 2000) => {
    setAnimatedStats(prev => {
      const start = prev[key];
      const startTime = Date.now();
      
      const updateNumber = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for dramatic effect
        const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        const current = Math.floor(start + (target - start) * easeOutExpo);
        
        setAnimatedStats(prevStats => ({ ...prevStats, [key]: current }));
        
        if (progress < 1) {
          requestAnimationFrame(updateNumber);
        }
      };
      
      requestAnimationFrame(updateNumber);
      return prev;
    });
  }, []);
  
  const fontSize = useBreakpointValue({ base: 'xl', md: '2xl', lg: '3xl' });
  const headingSize = useBreakpointValue({ base: '2xl', md: '4xl', lg: '5xl' });

  // Fetch live statistics from database
  const fetchLiveStats = useCallback(async () => {
    try {
      setLiveStats(prev => ({ ...prev, loading: true }));

      // Get blood draws completed (submissions with completed status)
      const { count: bloodDrawsCount, error: bloodDrawsError } = await supabase
        .from('submissions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed');

      // Get mobile phlebotomists count
      const { count: phlebotomistsCount, error: phlebotomistsError } = await supabase
        .from('phlebotomist_profiles')
        .select('*', { count: 'exact', head: true });

      // Get doctors count 
      const { count: doctorsCount, error: doctorsError } = await supabase
        .from('doctors')
        .select('*', { count: 'exact', head: true });

      // Get patients count
      const { count: patientsCount, error: patientsError } = await supabase
        .from('patients')
        .select('*', { count: 'exact', head: true });

      if (bloodDrawsError) console.error('Error fetching blood draws:', bloodDrawsError);
      if (phlebotomistsError) console.error('Error fetching phlebotomists:', phlebotomistsError);
      if (doctorsError) console.error('Error fetching doctors:', doctorsError);
      if (patientsError) console.error('Error fetching patients:', patientsError);

      const newStats = {
        bloodDraws: bloodDrawsCount || 0,
        phlebotomists: phlebotomistsCount || 0,
        doctors: doctorsCount || 0,
        patients: patientsCount || 0,
        loading: false
      };

      setLiveStats(newStats);

      // Trigger dramatic number animations only if stats changed
      if (newStats.bloodDraws !== liveStats.bloodDraws || 
          newStats.phlebotomists !== liveStats.phlebotomists || 
          newStats.doctors !== liveStats.doctors || 
          newStats.patients !== liveStats.patients) {
        setTimeout(() => {
          animateNumber(newStats.bloodDraws, 'bloodDraws', 2500);
          animateNumber(newStats.phlebotomists, 'phlebotomists', 2000);
          animateNumber(newStats.doctors, 'doctors', 1800);
          animateNumber(newStats.patients, 'patients', 2200);
        }, 500);
      }

    } catch (error) {
      console.error('Error fetching live stats:', error);
      setLiveStats(prev => ({ ...prev, loading: false }));
    }
  }, [animateNumber, liveStats.bloodDraws, liveStats.doctors, liveStats.patients, liveStats.phlebotomists]);

  // Slide data
  const slides = useMemo(() => [
    {
      id: 1,
      title: "AtHomeBloodDraw.com",
      content: "Connecting patients, mobile phlebotomists, laboratories, and doctors in a unified platform for at-home blood collection. We solve scheduling, logistics, insurance compatibility, and test routing challenges.",
      type: "title"
    },
    {
      id: 2,
      title: "Core Offering",
      content: "AtHomeBloodDraw.com provides a comprehensive, user-friendly platform that acts as the central hub for at-home blood collection. Our core offerings include a searchable test directory, which allows patients to easily find the specific lab tests they need, connecting them seamlessly with available mobile phlebotomists and partner laboratories. Additionally, we provide a streamlined, on-the-go workflow specifically designed for mobile phlebotomists, enabling efficient blood collection and expedited delivery to labs.",
      features: [
        { title: "Smart Scheduling", description: "AI-powered appointment matching" },
        { title: "Real-time Tracking", description: "GPS-enabled collection updates" },
        { title: "Test Directory", description: "Comprehensive lab test catalog" },
        { title: "Mobile Workflow", description: "Streamlined phlebotomist tools" }
      ],
      competitive: {
        points: [
          "First unified platform in market",
          "Real-time coordination & tracking",
          "Multi-stakeholder integration",
          "Scalable technology infrastructure"
        ]
      },
      processFlow: [
        { icon: FaUsers, title: "Patient Orders", description: "Easy search & book" },
        { icon: FaUserMd, title: "Mobile Draw", description: "At-home collection" },
        { icon: FaHospital, title: "Lab Processing", description: "Fast & accurate" },
        { icon: FaVial, title: "Results Delivered", description: "Real-time updates" },
      ],
      comparison: {
        current: {
          title: "Current Disconnected Process",
          points: [
            "Multiple platforms & manual coordination",
            "Delays & communication gaps",
            "Frustrated patients & providers"
          ]
        },
        solution: {
          title: "Our Seamless Solution",
          points: [
            "One unified platform & automated coordination",
            "Real-time connections & expedited logistics",
            "Enhanced patient & provider experience"
          ]
        }
      },
      type: "features"
    },
    {
      id: 3,
      title: "Revenue Streams",
      content: "Multiple revenue channels across all stakeholder groups ensure sustainable, diversified income",
      revenueStreams: [
        {
          stakeholder: "Mobile Phlebotomists",
          icon: FaUserMd,
          color: "green",
          streams: [
            "Commission per draw (Uber-style)",
            "Subscription for advanced tools",
            "Featured regional listing"
          ]
        },
        {
          stakeholder: "Patients",
          icon: FaUsers,
          color: "blue",
          streams: [
            "Per-visit dynamic pricing",
            "Premium and concierge plans",
            "Chronic care subscriptions"
          ]
        },
        {
          stakeholder: "Doctors",
          icon: FaHospital,
          color: "purple",
          streams: [
            "Monthly provider listing fees",
            "Per-patient match or telehealth referral fees",
            "White-labeled integration widget"
          ]
        },
        {
          stakeholder: "Laboratories",
          icon: FaVial,
          color: "orange",
          streams: [
            "Integration fees for lab systems",
            "Per-test processing fees",
            "Data analytics and reporting subscriptions"
          ]
        }
      ],
      type: "revenue"
    },
    {
      id: 4,
      title: "Live Company Stats",
      content: "These statistics are updated in real time to showcase platform traction:",
      type: "stats"
    },
    {
      id: 5,
      title: "Phlebotomists (Individual Phlebotomist Businesses)",
      content: "Addressing critical challenges faced by independent mobile phlebotomists in today's healthcare landscape.",
      problems: [
        "Difficulty in consistently acquiring new patients",
        "Challenges with optimizing routes and managing logistics efficiently", 
        "Major labs like Quest Diagnostics won't partner with individual, small mobile phlebotomist businesses"
      ],
      marketSize: {
        title: "Phlebotomist Market Size",
        stats: [
          "10,000 total licensed phlebotomists in the USA",
          "U.S. mobile phlebotomy market projected to exceed $500 million by 2027",
          "Rapid growth fueled by increasing demand for convenient, at-home healthcare services",
          "Broader shift towards decentralized care models creating significant opportunities"
        ]
      },
      solutions: [
        {
          title: "Patient Matching",
          description: "Matched with patients & labs by test/insurance compatibility",
          icon: FaUsers
        },
        {
          title: "Route Planning", 
          description: "Smart route planning to reduce downtime",
          icon: FaMapMarkerAlt
        },
        {
          title: "Lab Integration",
          description: "Simplified upload process to labs", 
          icon: FaVial
        }
      ],
      type: "mobile-labs"
    },
    {
      id: 6,
      title: "Patients",
      content: "Addressing critical pain points that patients face in accessing convenient, transparent healthcare services.",
      challenges: [
        {
          title: "Inconvenient Access",
          description: "Traditional blood draws require travel to clinics or hospitals, often during work hours, causing disruption and inconvenience for patients."
        },
        {
          title: "Lack of Price Transparency", 
          description: "Patients often struggle to compare prices and understand insurance coverage for different lab tests, leading to unexpected costs."
        },
        {
          title: "Complex Scheduling",
          description: "Booking appointments for blood draws can be a cumbersome process, involving phone calls and long wait times."
        }
      ],
      solutions: [
        {
          title: "Convenient Access",
          description: "Find phlebotomists near them for lower-cost, convenient draws",
          icon: FaMapMarkerAlt
        },
        {
          title: "Price Comparison",
          description: "Compare labs by price, coverage, and test availability", 
          icon: FaChartLine
        },
        {
          title: "Easy Scheduling",
          description: "Uber-style scheduling for lower costs",
          icon: FaClock
        }
      ],
      marketOverview: "The demand for convenient, at-home healthcare services is rapidly growing, driven by an aging population, increased awareness of preventative health, and technological advancements. Patients seek alternatives to traditional healthcare settings that offer flexibility, transparency, and comfort. Our platform addresses this evolving market need by providing a streamlined, accessible solution for essential diagnostic services, directly appealing to patients seeking modern healthcare delivery.",
      type: "patients"
    },
    {
      id: 7,
      title: "Laboratories",
      subtitle: "Problem, Opportunity, Solution",
      type: "laboratories",
      content: "Transforming how laboratories manage specimen collection and client acquisition through our unified platform.",
      problems: [
        {
          title: "Diverse Test Complexities",
          description: "Struggle with diverse test types and insurance complexities."
        },
        {
          title: "Cumbersome Onboarding",
          description: "Cumbersome client onboarding process."
        },
        {
          title: "Administrative Overhead",
          description: "High administrative overhead limits acquiring smaller accounts."
        }
      ],
      opportunity: {
        title: "Market Opportunity",
        points: [
          "Rapidly growing demand for convenient at-home healthcare.",
          "Fragmented current specimen collection system.",
          "Significant opportunity for a unified platform."
        ]
      },
      solutions: [
        {
          title: "Comprehensive Directory",
          description: "Provides a comprehensive test directory that simplifies everything.",
          icon: FaClipboardList
        },
        {
          title: "Pre-Qualified Specimens",
          description: "Pre-qualifies specimens to meet lab criteria.",
          icon: FaCheckCircle
        },
        {
          title: "Reduced Admin Burden",
          description: "Reduces administrative burden and accelerates acquisition.",
          icon: FaUserTie
        },
        {
          title: "Increased Test Volume",
          description: "Increases test volume, allowing labs to focus on analysis.",
          icon: FaChartLine
        }
      ]
    },
    {
      id: 8,
      title: "Rollout Timeline",
      type: "timeline",
      content: "Our strategic rollout plan focuses on building a strong foundation with laboratories first, then expanding to mobile phlebotomists, and finally launching to patients.",
      timeline: [
        {
          phase: "Phase 1: Laboratories",
          timeframe: "Now",
          description: "Launch with partner laboratories to establish test processing capabilities and build the foundation of our platform.",
          icon: FaVial,
          color: "#00d4ff",
          status: "current"
        },
        {
          phase: "Phase 2: Phlebotomists",
          timeframe: "3 Months",
          description: "Onboard mobile phlebotomists to provide at-home collection services and expand our geographic coverage.",
          icon: FaUserMd,
          color: "#9333ea",
          status: "upcoming"
        },
        {
          phase: "Phase 3: Patients",
          timeframe: "6 Months",
          description: "Launch patient-facing platform for easy booking, scheduling, and test result access.",
          icon: FaUsers,
          color: "#ff6b6b",
          status: "future"
        }
      ]
    },
    {
      id: 9,
      title: "Market Potential",
      type: "market",
      content: "The demand for convenient at-home healthcare services is rapidly expanding, presenting a significant opportunity for AtHomeBloodDraw.com.",
      marketData: [
        {
          title: "US Diagnostic Market",
          value: "$240B",
          subtitle: "Total Addressable Market",
          description: "The US diagnostic testing market represents a massive opportunity for disruption",
          color: "#00d4ff",
          icon: FaChartLine
        },
        {
          title: "At-Home Testing CAGR",
          value: "15.8%",
          subtitle: "Annual Growth Rate",
          description: "Rapid expansion of at-home testing services driven by consumer demand",
          color: "#9333ea",
          icon: FaMapMarkerAlt
        },
        {
          title: "Chronic Disease Patients",
          value: "133M",
          subtitle: "Americans with Chronic Conditions",
          description: "Large patient population requiring regular monitoring and testing",
          color: "#ff6b6b",
          icon: FaUsers
        }
      ]
    },
    {
      id: 10,
      title: "Market Validation: Getlabs.com",
      type: "validation",
      content: "Getlabs is a significant player in the at-home lab collection market, demonstrating strong investor confidence and validating the growing demand for convenient, in-home healthcare services.",
      conclusion: "Getlabs has successfully partnered with major health systems and labs, proving the viability and necessity of integrated mobile phlebotomy services. Their trajectory reinforces the immense potential for AtHomeBloodDraw.com to capture a significant share of this expanding market.",
      validationMetrics: [
        {
          title: "Total Funding Raised",
          value: "$58M+",
          subtitle: "Series A & Seed Rounds",
          description: "This substantial investment highlights the market's belief in the at-home lab testing model.",
          color: "#00d4ff",
          icon: FaDollarSign
        },
        {
          title: "Markets Served",
          value: "45+",
          subtitle: "Major U.S. Cities",
          description: "Their extensive reach across the U.S. showcases the scalability and broad appeal of this service.",
          color: "#9333ea",
          icon: FaMapMarkerAlt
        },
        {
          title: "Revenue",
          value: "$25M+",
          subtitle: "Annual Revenue Run Rate",
          description: "This significant revenue demonstrates strong market performance and demand for at-home lab services.",
          color: "#ff6b6b",
          icon: FaChartLine
        }
      ]
    },
    {
      id: 11,
      title: "Thank You!",
      type: "thankyou", 
      content: "We are dedicated to transforming healthcare access and convenience.",
      website: "Visit AtHomeBloodDraw.com to learn more about our platform.",
      calendlyUrl: "https://calendly.com/contact-athomeblooddraw/30min"
    }
  ], []);

  // Fetch stats when component mounts
  useEffect(() => {
    fetchLiveStats();
  }, [fetchLiveStats]);

  // Preload Calendly script on component mount
  useEffect(() => {
    // Load Calendly script immediately when component mounts
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Initialize Calendly widget when thank you slide is active
  useEffect(() => {
    const currentSlideData = slides[currentSlide];
    if (currentSlideData && currentSlideData.type === 'thankyou') {
      const initializeCalendly = () => {
        const widgetElement = document.querySelector('.calendly-inline-widget');
        const fallbackElement = document.querySelector('.calendly-fallback');
        
        if (window.Calendly && widgetElement) {
          // Clear any existing widget first
          widgetElement.innerHTML = '';
          
          // Initialize the widget
          window.Calendly.initInlineWidget({
            url: currentSlideData.calendlyUrl,
            parentElement: widgetElement
          });
          
          // Hide fallback content once widget is initialized
          if (fallbackElement) {
            setTimeout(() => {
              fallbackElement.style.display = 'none';
            }, 1000); // Give widget time to load
          }
        } else if (!window.Calendly) {
          // If Calendly isn't loaded yet, try again in a bit
          setTimeout(initializeCalendly, 300);
        }
      };

      // Small delay to ensure DOM is ready
      setTimeout(initializeCalendly, 100);
    }
  }, [currentSlide, slides]);

  // Set up real-time subscriptions for live updates
  useEffect(() => {
    const subscriptions = [];

    // Subscribe to submissions changes
    const submissionsSubscription = supabase
      .channel('submissions_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'submissions' 
      }, () => {
        fetchLiveStats();
      })
      .subscribe();

    // Subscribe to phlebotomist_profiles changes
    const phlebotomistsSubscription = supabase
      .channel('phlebotomists_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'phlebotomist_profiles' 
      }, () => {
        fetchLiveStats();
      })
      .subscribe();

    // Subscribe to doctors changes
    const doctorsSubscription = supabase
      .channel('doctors_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'doctors' 
      }, () => {
        fetchLiveStats();
      })
      .subscribe();

    // Subscribe to patients changes
    const patientsSubscription = supabase
      .channel('patients_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'patients' 
      }, () => {
        fetchLiveStats();
      })
      .subscribe();

    subscriptions.push(submissionsSubscription, phlebotomistsSubscription, doctorsSubscription, patientsSubscription);

    // Cleanup subscriptions on unmount
    return () => {
      subscriptions.forEach(subscription => {
        supabase.removeChannel(subscription);
      });
    };
  }, [fetchLiveStats]);

  // Refresh stats when on stats slide
  useEffect(() => {
    if (slides[currentSlide]?.type === 'stats') {
      fetchLiveStats();
    }
  }, [currentSlide, fetchLiveStats, slides]);

  const totalSlides = slides.length;

  // Navigation functions - No loop
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => prev < totalSlides - 1 ? prev + 1 : prev);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => prev > 0 ? prev - 1 : prev);
  }, []);

  const handleNext = useCallback(() => {
    nextSlide();
  }, [nextSlide]);

  const handlePrev = useCallback(() => {
    prevSlide();
  }, [prevSlide]);


  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'Escape':
          e.preventDefault();
          setIsAutoPlaying(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextSlide, prevSlide]);

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000); // 5 seconds per slide
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const currentSlideData = slides[currentSlide];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const slideTransition = {
    x: { type: "spring", stiffness: 300, damping: 30 },
    opacity: { duration: 0.2 }
  };

  return (
    <>
      {/* CSS Keyframes */}
      <style>
        {`
          @keyframes floatAnimation {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(1deg); }
          }
          
          @keyframes pulseGlow {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.1); }
          }
          
          @keyframes shimmer {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }
        `}
      </style>

      <Box
        minH="100vh"
        bgGradient={bgGradient}
        position="relative"
        overflow="hidden"
        overflowY={{ base: "auto", md: "hidden" }}
      >
        {/* Animated Background Pattern */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.15}
          bgImage="radial-gradient(circle at 20% 20%, #00d4ff 2px, transparent 2px), radial-gradient(circle at 80% 80%, #ff6b6b 2px, transparent 2px), radial-gradient(circle at 40% 70%, #4ecdc4 1px, transparent 1px)"
          bgSize="60px 60px, 80px 80px, 40px 40px"
          animation="floatAnimation 20s ease-in-out infinite"
          zIndex={0}
        />
        
        {/* Dramatic Glow Effects */}
        <Box
          position="absolute"
          top="20%"
          left="10%"
          w="300px"
          h="300px"
          bg="radial-gradient(circle, rgba(0, 212, 255, 0.3) 0%, transparent 70%)"
          borderRadius="full"
          filter="blur(80px)"
          animation="pulseGlow 4s ease-in-out infinite"
          zIndex={0}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          bg="radial-gradient(circle, rgba(255, 107, 107, 0.2) 0%, transparent 70%)"
          borderRadius="full"
          filter="blur(100px)"
          animation="pulseGlow 6s ease-in-out infinite reverse"
          zIndex={0}
        />

        {/* Progress Bar */}
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={1000}
          bg="rgba(15, 15, 35, 0.9)"
          boxShadow="0 4px 32px rgba(0, 212, 255, 0.3)"
          backdropFilter="blur(20px)"
          border="1px solid rgba(0, 212, 255, 0.2)"
          px={4}
          py={2}
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="bold" color="#00d4ff">
              {currentSlide + 1} / {totalSlides}
            </Text>
            <Box flex={1} mx={4}>
              <Progress
                value={((currentSlide + 1) / totalSlides) * 100}
                bg="rgba(255, 255, 255, 0.1)"
                borderRadius="full"
                size="sm"
                sx={{
                  '& > div': {
                    background: 'linear-gradient(90deg, #00d4ff, #ff6b6b)',
                    boxShadow: '0 0 20px rgba(0, 212, 255, 0.5)'
                  }
                }}
              />
            </Box>

          </HStack>
        </Box>

        {/* Main Slide Content */}
        {/* Restore card design and clean up navigation */}
        <Container
          maxW={{ base: '100vw', md: '6xl' }}
          px={{ base: 0, md: 8 }}
          pt={{ base: 0, md: 12 }}
          pb={{ base: 0, md: 36 }}
          minH={{ base: '100vh', md: 'calc(100vh - 120px)' }}
          display="flex"
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          bg={{ base: 'white', md: 'gray.100' }}
        >
          {/* Subtle blue top border/gradient for mobile */}
          <Box display={{ base: 'block', md: 'none' }} w="full" h="6px" bgGradient="linear(to-r, #0066cc, #00d4ff)" mb={2} />
          <Box
            position="relative"
            w="full"
            maxW={{ base: '100vw', md: '5xl' }}
            minH={{ base: '70vh', md: '75vh' }}
            maxH={{ base: '85vh', md: '90vh' }}
            bg="white"
            borderRadius={{ base: 'none', md: '2xl' }}
            boxShadow={{ base: 'none', md: 'lg' }}
            p={{ base: 4, md: 8 }}
            display="flex"
            flexDir="column"
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Progress bar and slide number */}
            <Flex w="full" align="center" mb={4}>
              <Text fontWeight="bold" fontSize="lg" color="blue.700">{currentSlide + 1} / {slides.length}</Text>
              <Progress value={((currentSlide + 1) / slides.length) * 100} flex={1} mx={3} size="sm" colorScheme="blue" borderRadius="md" />
            </Flex>
            {/* Slide content */}
            <Box w="full" fontSize={{ base: '1.1em', md: '1.1em' }} color="#222" flex="1" textAlign="left" display="flex" flexDir="column" justifyContent="center">
              {currentSlideData?.icon && (
                <Box mb={2} fontSize="2.5em" color="blue.500">{currentSlideData.icon}</Box>
              )}
              {currentSlideData?.image && (
                <Image src={currentSlideData.image} alt="slide image" mb={2} borderRadius="md" maxH="180px" mx="auto" />
              )}
              {currentSlideData?.title && (
                <Heading as="h2" size="lg" mb={2} color="blue.700">{currentSlideData.title}</Heading>
              )}
              {currentSlideData?.subtitle && (
                <Text fontSize="md" color="gray.500" mb={2}>{currentSlideData.subtitle}</Text>
              )}
              {currentSlideData?.content && (
                <Text mb={2}>{currentSlideData.content}</Text>
              )}
              {/* Render features, stats, revenue, etc. if present */}
              {currentSlideData?.features && Array.isArray(currentSlideData.features) && (
                <VStack align="start" spacing={2} mb={3}>
                  {currentSlideData.features.map((f, idx) => (
                    <HStack key={idx} align="center">
                      <Icon as={f.icon} color="green.400" />
                      <Text fontWeight="bold">{f.title}:</Text>
                      <Text>{f.description}</Text>
                    </HStack>
                  ))}
                </VStack>
              )}
              {currentSlideData?.processFlow && Array.isArray(currentSlideData.processFlow) && (
                <HStack spacing={6} mb={3} justify="center">
                  {currentSlideData.processFlow.map((step, i) => (
                    <VStack key={i} align="center">
                      <Icon as={step.icon} boxSize={8} color="blue.400" />
                      <Text fontWeight="bold" fontSize="sm">{step.title}</Text>
                      <Text fontSize="xs">{step.description}</Text>
                    </VStack>
                  ))}
                </HStack>
              )}
              {currentSlideData?.revenueStreams && Array.isArray(currentSlideData.revenueStreams) && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={3}>
                  {currentSlideData.revenueStreams.map((r, i) => (
                    <Box key={i} p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                      <HStack mb={1}>
                        <Icon as={r.icon} color={r.color + '.400'} />
                        <Text fontWeight="bold">{r.stakeholder}</Text>
                      </HStack>
                      <VStack align="start" spacing={1} pl={6}>
                        {r.streams.map((s, j) => (
                          <Text key={j} fontSize="sm">- {s}</Text>
                        ))}
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
              {currentSlideData?.comparison && (
                <>
                  <Heading as="h3" size="md" mb={2} color="blue.600">Comparison</Heading>
                  <VStack align="start" spacing={2} mb={3}>
                    <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                      <Heading as="h4" size="md" mb={1} color="purple.600">{currentSlideData.comparison.current.title}</Heading>
                      <VStack align="start" spacing={1} pl={4}>
                        {currentSlideData.comparison.current.points.map((p, i) => (
                          <Text key={i} fontSize="sm">- {p}</Text>
                        ))}
                      </VStack>
                    </Box>
                    <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                      <Heading as="h4" size="md" mb={1} color="purple.600">{currentSlideData.comparison.solution.title}</Heading>
                      <VStack align="start" spacing={1} pl={4}>
                        {currentSlideData.comparison.solution.points.map((p, i) => (
                          <Text key={i} fontSize="sm">- {p}</Text>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                </>
              )}
              {/* Stats Slide */}
              {currentSlideData?.type === 'stats' && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={3}>
                  <VStack><Icon as={FaVial} color="blue.400" boxSize={7} /><Text fontWeight="bold">Blood Draws</Text><Text fontSize="lg">{animatedStats.bloodDraws}</Text></VStack>
                  <VStack><Icon as={FaUserMd} color="green.400" boxSize={7} /><Text fontWeight="bold">Phlebotomists</Text><Text fontSize="lg">{animatedStats.phlebotomists}</Text></VStack>
                  <VStack><Icon as={FaHospital} color="purple.400" boxSize={7} /><Text fontWeight="bold">Doctors</Text><Text fontSize="lg">{animatedStats.doctors}</Text></VStack>
                  <VStack><Icon as={FaUsers} color="orange.400" boxSize={7} /><Text fontWeight="bold">Patients</Text><Text fontSize="lg">{animatedStats.patients}</Text></VStack>
                </SimpleGrid>
              )}
              {/* Phlebotomists Slide */}
              {currentSlideData?.type === 'mobile-labs' && (
                <>
                  <VStack align="start" spacing={1} mb={2}>{currentSlideData.problems?.map((p, i) => (<Text key={i}>- {p}</Text>))}</VStack>
                  <VStack align="start" spacing={1} mb={2}>{currentSlideData.marketSize?.stats?.map((s, i) => (<Text key={i} fontSize="sm">{s}</Text>))}</VStack>
                  <HStack spacing={4} mb={2}>{currentSlideData.solutions?.map((sol, i) => (<VStack key={i}><Icon as={sol.icon} color="blue.400" boxSize={6} /><Text fontWeight="bold" fontSize="sm">{sol.title}</Text><Text fontSize="xs">{sol.description}</Text></VStack>))}</HStack>
                </>
              )}
              {/* Patients Slide */}
              {currentSlideData?.type === 'patients' && (
                <>
                  <VStack align="start" spacing={1} mb={2}>{currentSlideData.challenges?.map((c, i) => (<Text key={i}>- {c.title}: {c.description}</Text>))}</VStack>
                  <HStack spacing={4} mb={2}>{currentSlideData.solutions?.map((sol, i) => (<VStack key={i}><Icon as={sol.icon} color="blue.400" boxSize={6} /><Text fontWeight="bold" fontSize="sm">{sol.title}</Text><Text fontSize="xs">{sol.description}</Text></VStack>))}</HStack>
                  <Text fontSize="sm" color="gray.500">{currentSlideData.marketOverview}</Text>
                </>
              )}
              {/* Laboratories Slide */}
              {currentSlideData?.type === 'laboratories' && (
                <>
                  <VStack align="start" spacing={1} mb={2}>{currentSlideData.problems?.map((c, i) => (<Text key={i}>- {c.title}: {c.description}</Text>))}</VStack>
                  <VStack align="start" spacing={1} mb={2}>{currentSlideData.opportunity?.points?.map((p, i) => (<Text key={i} fontSize="sm">{p}</Text>))}</VStack>
                  <HStack spacing={4} mb={2}>{currentSlideData.solutions?.map((sol, i) => (<VStack key={i}><Icon as={sol.icon} color="blue.400" boxSize={6} /><Text fontWeight="bold" fontSize="sm">{sol.title}</Text><Text fontSize="xs">{sol.description}</Text></VStack>))}</HStack>
                </>
              )}
              {/* Market Slide */}
              {currentSlideData?.type === 'market' && (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={3}>{currentSlideData.marketData?.map((d, i) => (<VStack key={i} p={3} borderWidth={1} borderRadius="md" bg="gray.50"><Icon as={d.icon} color={d.color} boxSize={7} /><Text fontWeight="bold">{d.title}</Text><Text fontSize="lg">{d.value}</Text><Text fontSize="sm">{d.subtitle}</Text><Text fontSize="xs">{d.description}</Text></VStack>))}</SimpleGrid>
              )}
              {/* Timeline Slide */}
              {currentSlideData?.type === 'timeline' && (
                <VStack spacing={6} align="stretch" mt={4}>
                  {currentSlideData.timeline?.map((phase, i) => (
                    <Box key={i} position="relative">
                      {/* Timeline line */}
                      {i < currentSlideData.timeline.length - 1 && (
                        <Box
                          position="absolute"
                          left="24px"
                          top="48px"
                          w="2px"
                          h="calc(100% + 24px)"
                          bg="gray.200"
                          zIndex={0}
                        />
                      )}
                      <HStack spacing={4} align="flex-start">
                        {/* Timeline dot */}
                        <Box
                          position="relative"
                          zIndex={1}
                          w="48px"
                          h="48px"
                          borderRadius="full"
                          bg={phase.status === 'current' ? phase.color : 'gray.300'}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          boxShadow="md"
                        >
                          <Icon as={phase.icon} color="white" boxSize={6} />
                        </Box>
                        {/* Phase content */}
                        <VStack align="start" spacing={2} flex={1}>
                          <HStack spacing={3} align="center">
                            <Text fontSize="lg" fontWeight="bold" color={phase.color}>
                              {phase.phase}
                            </Text>
                            <Box
                              px={3}
                              py={1}
                              borderRadius="full"
                              bg={phase.status === 'current' ? `${phase.color}.100` : 'gray.100'}
                              color={phase.status === 'current' ? phase.color : 'gray.600'}
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              {phase.timeframe}
                            </Box>
                          </HStack>
                          <Text color="gray.600" fontSize="md">
                            {phase.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
              {/* Validation Slide */}
              {currentSlideData?.type === 'validation' && (
                <>
                  <Text mb={2}>{currentSlideData.conclusion}</Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={3}>{currentSlideData.validationMetrics?.map((d, i) => (<VStack key={i} p={3} borderWidth={1} borderRadius="md" bg="gray.50"><Icon as={d.icon} color={d.color} boxSize={7} /><Text fontWeight="bold">{d.title}</Text><Text fontSize="lg">{d.value}</Text><Text fontSize="sm">{d.subtitle}</Text><Text fontSize="xs">{d.description}</Text></VStack>))}</SimpleGrid>
                </>
              )}
              {/* Thank You Slide */}
              {currentSlideData?.type === 'thankyou' && (
                <VStack spacing={4} align="center" mt={2}>
                  <Text fontSize="xl" color="blue.600" textAlign="center">Thank you for viewing our pitch deck!</Text>
                  <Text textAlign="center">{currentSlideData.website}</Text>
                  <Box className="calendly-inline-widget" style={{ minWidth: 320, height: 350 }} />
                  <Box className="calendly-fallback" mt={2}>
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      If the booking widget does not load, <a href={currentSlideData.calendlyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>click here to book a call</a>.
                    </Text>
                  </Box>
                </VStack>
              )}
            </Box>
            {/* Navigation dots and controls (single set, bottom) */}
            <Flex w="full" direction="column" align="center" mt={4}>
              <HStack spacing={2} mb={3}>
                {slides.map((_, idx) => (
                  <Box
                    key={idx}
                    w={3}
                    h={3}
                    borderRadius="full"
                    bg={idx === currentSlide ? 'blue.400' : 'gray.300'}
                    border={idx === currentSlide ? '2px solid #3182ce' : 'none'}
                    transition="all 0.2s"
                  />
                ))}
              </HStack>
              <Flex w="full" justify="space-between" align="center">
                <Button
                  onClick={handlePrev}
                  isDisabled={currentSlide === 0}
                  colorScheme="blue"
                  variant="solid"
                  size="lg"
                  minW={24}
                >
                  Prev
                </Button>
                <Text fontWeight="bold" color="blue.700">{currentSlide + 1} / {slides.length}</Text>
                <Button
                  onClick={handleNext}
                  isDisabled={currentSlide === slides.length - 1}
                  colorScheme="blue"
                  variant="solid"
                  size="lg"
                  minW={24}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Container>

        {/* Enhanced Instructions: Only show on desktop */}
        <MotionBox
          display={{ base: 'none', md: 'block' }}
          position="fixed"
          bottom={4}
          right={4}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2 }}
        >
          <VStack
            spacing={2}
            p={4}
            bg="rgba(255, 255, 255, 0.95)"
            borderRadius="xl"
            boxShadow="0 4px 24px rgba(0, 212, 255, 0.3)"
            border="1px solid rgba(0, 212, 255, 0.3)"
            backdropFilter="blur(20px)"
            fontSize="xs"
            color="gray.700"
            opacity={0.8}
            _hover={{ opacity: 1, transform: "scale(1.02)" }}
            transition="all 0.3s ease"
          >
            <HStack spacing={2} align="center">
              <Icon as={FaChartLine} color="#00d4ff" />
              <Text fontWeight="bold" color="#00d4ff">Navigation:</Text>
            </HStack>
            <VStack spacing={1} align="start" fontSize="10px">
              <Text><strong>Arrows:</strong> Navigate slides</Text>
              <Text><strong>Dots:</strong> Jump to slide</Text>
              <Text><strong>Keyboard:</strong> ← → keys</Text>
            </VStack>
          </VStack>
        </MotionBox>
      </Box>
    </>
  );
};

export default PitchDeck;